<template>
  <header
    class="fixed left-0 top-0 w-full z-9999 py-7 lg:py-0"
    :class="{
      'bg-dark/70 backdrop-blur-lg shadow !py-4 lg:!py-0 transition duration-100 before:absolute before:w-full before:h-[1px] before:bottom-0 before:left-0 before:features-row-border':
        stickyMenu,
      'navbar-hidden': mobileNavHidden && !navigationOpen,
    }"
    @scroll.window="handleScroll"
  >
    <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 lg:flex items-center justify-between relative lg:gap-10">
      <div class="w-full lg:w-auto flex items-center justify-between">
        <router-link to="/" class="navbar-logo block flex-shrink-0" style="min-width: 140px; margin-right: 40px;">
          <img :src="logoSrc" :alt="$t('auth.footer.logoAlt')" style="width: 120px; height: auto; display: block;" />
        </router-link>

        <!-- Mobile: Gallery + Tutorial + Hamburger buttons -->
        <div class="mobile-nav-buttons flex items-center gap-3 lg:hidden">
          <!-- Compact credits chip -->
          <router-link
            v-if="loggedIn && !navigationOpen"
            to="/profile"
            class="mobile-credits-chip"
            :title="$t('navbar.credits.label') + ': ' + userCredits"
          >
            <i class="fas fa-coins"></i>
            <span>{{ userCredits }}</span>
          </router-link>
          <!-- Language picker (mobile) -->
          <button
            v-if="!navigationOpen"
            class="mobile-lang-btn"
            :title="$t('navbar.language.tooltip')"
            @click="langModalOpen = true"
          >
            <i class="fas fa-globe"></i>
            <span class="mobile-lang-code">{{ currentLangCode }}</span>
          </button>
          <!-- Gallery button (mobile only) -->
          <router-link
            v-if="loggedIn && !navigationOpen"
            to="/gallery"
            class="mobile-tutorial-btn mobile-gallery-btn"
            :title="$t('navbar.gallery')"
          >
            <i class="fas fa-images"></i>
          </router-link>
          <button @click="navigationOpen = !navigationOpen">
          <span class="block relative cursor-pointer w-5.5 h-5.5">
            <span class="du-block absolute right-0 w-full h-full">
              <span
                class="block relative top-0 left-0 bg-white rounded-sm w-0 h-0.5 my-1 ease-in-out duration-200 delay-[0]"
                :class="{ '!w-full delay-300': !navigationOpen }"
              ></span>
              <span
                class="block relative top-0 left-0 bg-white rounded-sm w-0 h-0.5 my-1 ease-in-out duration-200 delay-150"
                :class="{ '!w-full delay-400': !navigationOpen }"
              ></span>
              <span
                class="block relative top-0 left-0 bg-white rounded-sm w-0 h-0.5 my-1 ease-in-out duration-200 delay-200"
                :class="{ '!w-full delay-500': !navigationOpen }"
              ></span>
            </span>
            <span class="du-block absolute right-0 w-full h-full rotate-45">
              <span
                class="block bg-white rounded-sm ease-in-out duration-200 delay-300 absolute left-2.5 top-0 w-0.5 h-full"
                :class="{ '!h-0 delay-[0]': !navigationOpen }"
              ></span>
              <span
                class="block bg-white rounded-sm ease-in-out duration-200 delay-400 absolute left-0 top-2.5 w-full h-0.5"
                :class="{ '!h-0 delay-200': !navigationOpen }"
              ></span>
            </span>
          </span>
        </button>
        </div>
      </div>

      <!-- Mobile menu backdrop (click to close) -->
      <div 
        v-if="navigationOpen"
        class="mobile-backdrop lg:hidden"
        @click="navigationOpen = false"
      ></div>

      <div
        class="mobile-menu-panel w-full lg:w-auto lg:flex-1 h-0 lg:h-auto invisible lg:visible lg:flex items-center justify-between"
        :class="{ '!visible bg-dark shadow-lg relative !h-auto max-h-[400px] overflow-y-scroll rounded-md mt-4 p-7.5': navigationOpen }"
      >
        <!-- Mobile close button -->
        <button 
          v-if="navigationOpen" 
          class="mobile-close-btn lg:hidden"
          @click="navigationOpen = false"
        >
          <i class="fas fa-times"></i>
        </button>

        <nav>
          <ul class="flex lg:items-center flex-col lg:flex-row gap-5 lg:gap-2">
            <!-- Home Link -->
            <li class="nav__menu lg:py-7" :class="{ 'lg:!py-4': stickyMenu }">
              <router-link
                to="/"
                class="relative text-white/80 text-sm py-1.5 px-4 border border-transparent hover:text-white hover:nav-gradient"
                active-class="!text-white nav-gradient"
              >
                {{ $t('navbar.home') }}
              </router-link>
            </li>

            <!-- Face Swap Link -->
            <li class="nav__menu lg:py-7" :class="{ 'lg:!py-4': stickyMenu }">
              <router-link
                to="/faceswap"
                class="sml relative text-white/80 text-sm py-1.5 px-4 border border-transparent hover:text-white hover:nav-gradient"
                active-class="!text-white nav-gradient"
              >
                {{ $t('navbar.faceswap') }}
              </router-link>
            </li>

            <!-- Imagine Studio (AI image generation & editing) -->
            <li class="nav__menu lg:py-7" :class="{ 'lg:!py-4': stickyMenu }">
              <router-link
                to="/imagine-studio"
                class="sml relative text-white/80 text-sm py-1.5 px-4 border border-transparent hover:text-white hover:nav-gradient"
                active-class="!text-white nav-gradient"
              >
                {{ $t('navbar.imagineStudio') }}
              </router-link>
            </li>

            <!-- Video Editor Studio Link -->
            <li class="nav__menu lg:py-7" :class="{ 'lg:!py-4': stickyMenu }">
              <router-link
                to="/studio"
                class="sml relative text-white/80 text-sm py-1.5 px-4 border border-transparent hover:text-white hover:nav-gradient"
                active-class="!text-white nav-gradient"
              >
                {{ $t('navbar.studio') }}
              </router-link>
            </li>

            <!-- Slow Motion + Image Edit moved into the Profile dropdown (below) -->

            <!-- Language picker (desktop) -->
            <li
              class="nav__menu lg:py-7"
              :class="{ 'lg:!py-4': stickyMenu }"
            >
              <button
                class="lang-nav-btn"
                :title="$t('navbar.language.tooltip')"
                @click="langModalOpen = true"
              >
                <i class="fas fa-globe"></i>
                <span>{{ currentLangCode }}</span>
              </button>
            </li>

            <!-- Gallery Icon (collapsed into Profile dropdown on small desktop <1280px) -->
            <li
              v-if="loggedIn"
              class="nav__menu lg:py-7 collapsible-icon"
              :class="{ 'lg:!py-4': stickyMenu }"
            >
              <router-link
                to="/gallery"
                class="support-nav-icon"
                :title="$t('navbar.gallery')"
              >
                <i class="fas fa-images"></i>
              </router-link>
            </li>

            <!-- Support Icon (collapsed into Profile dropdown on small desktop <1280px) -->
            <li
              v-if="loggedIn"
              class="nav__menu lg:py-7 collapsible-icon"
              :class="{ 'lg:!py-4': stickyMenu }"
            >
              <router-link
                id="tuto-support-btn"
                to="/support-center"
                class="support-nav-icon"
                :title="$t('navbar.support')"
                @click="fetchSupportUnread"
              >
                <i class="fas fa-headset"></i>
                <span v-if="supportUnreadCount > 0" class="support-nav-badge">{{ supportUnreadCount }}</span>
              </router-link>
            </li>


            <!-- New Credits Display (clean "{n} cr · {m} min" format) -->
            <li
              v-if="loggedIn"
              class="nav__menu lg:py-7"
              :class="{ 'lg:!py-4': stickyMenu }"
            >
              <span
                class="sml relative !text-white text-sm py-1.5 px-4 border border-transparent nav-gradient flex items-center gap-1.5"
                :title="$t('navbar.credits.label') + ': ' + userCredits"
              >
                <i class="fas fa-coins"></i>
                {{ $t('navbar.credits.chipShort', { n: userCredits }) }}
              </span>
            </li>

            <!-- Profile Dropdown -->
            <li
              v-if="loggedIn"
              class="group relative lg:py-7"
              :class="{ 'lg:!py-4': stickyMenu }"
              @click="dropdown = !dropdown"
            >
              <button
                type="button"
                class="relative text-white/80 text-sm py-1.5 px-4 border border-transparent hover:text-white hover:nav-gradient flex items-center justify-between gap-3 whitespace-nowrap bg-transparent"
              >
                <i class="fas fa-user"></i>
                <span class="username-text">{{ userInfo?.username || 'Guest' }}</span>
                <svg
                  class="fill-current w-3 h-3 cursor-pointer"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <path
                    d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"
                  />
                </svg>
              </button>

              <!-- Dropdown Menu -->
              <ul
                class="dropdown"
                :class="{ 'flex': dropdown, 'hidden lg:hidden': !dropdown && !navigationOpen }"
              >
                <!-- account summary: who you are, your plan, your credits -->
                <li class="nav-acct">
                  <span class="nav-acct-avatar">{{ (userInfo?.username || 'G').charAt(0).toUpperCase() }}</span>
                  <span class="nav-acct-info">
                    <b class="nav-acct-name">{{ userInfo?.username || 'Guest' }}</b>
                    <span class="nav-acct-meta">
                      <em class="nav-acct-plan">{{ planName }}</em>
                      <em class="nav-acct-credits"><i class="fas fa-coins"></i> {{ userCredits }}</em>
                    </span>
                  </span>
                </li>
                <li>
                  <router-link
                    to="/profile"
                    class="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-2 px-4.5 shadow-button hover:button-gradient-hover hover:shadow-none"
                  >
                    <i class="fas fa-user"></i>
                    {{ $t('navbar.profile') }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    to="/subscriptions"
                    class="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-2 px-4.5 shadow-button hover:button-gradient-hover hover:shadow-none"
                  >
                    <i class="fas fa-store"></i>
                    {{ $t('navbar.store') }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    to="/gallery"
                    class="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-2 px-4.5 shadow-button hover:button-gradient-hover hover:shadow-none"
                  >
                    <i class="fas fa-images"></i>
                    {{ $t('navbar.gallery') }}
                  </router-link>
                </li>
                <!-- Slow Motion (moved out of the top bar) -->
                <li>
                  <router-link
                    to="/slow-motion"
                    class="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-2 px-4.5 shadow-button hover:button-gradient-hover hover:shadow-none"
                  >
                    <i class="fas fa-film"></i>
                    {{ $t('navbar.slowMotion') }}
                  </router-link>
                </li>
                <!-- Image Edit / Photo Restore Legacy (moved out of the top bar) -->
                <li>
                  <router-link
                    to="/image-restore"
                    class="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-2 px-4.5 shadow-button hover:button-gradient-hover hover:shadow-none"
                  >
                    <i class="fas fa-image"></i>
                    {{ $t('navbar.imageEdit') }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    to="/support-center"
                    class="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-2 px-4.5 shadow-button hover:button-gradient-hover hover:shadow-none"
                  >
                    <i class="fas fa-headset"></i>
                    {{ $t('navbar.support') }}
                    <span v-if="supportUnreadCount > 0" class="support-badge">{{ supportUnreadCount }}</span>
                  </router-link>
                </li>
                <li class="lg:hidden">
                  <button
                    type="button"
                    class="button-border-gradient relative rounded-lg text-white text-sm flex items-center gap-1.5 py-2 px-4.5 shadow-button hover:button-gradient-hover hover:shadow-none"
                    @click.stop="openLangModalFromMenu"
                  >
                    <i class="fas fa-globe"></i>
                    {{ $t('navbar.language.label') }}: {{ currentLangCode }}
                  </button>
                </li>
                <li class="promo-cta-li">
                  <a
                    href="https://www.reddit.com/r/iSamurai_face_swap/"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="promo-cta-btn"
                  >
                    <i class="fas fa-bullhorn"></i>
                    <span>Join for PromoCodes &amp; Updates</span>
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </nav>

        <!-- Logout Button -->
        <div v-if="loggedIn" class="flex items-center gap-6 mt-7 lg:mt-0">
          <button
            @click="logout"
            class="button-border-gradient relative rounded-full text-white flex items-center justify-center w-9 h-9 shadow-button hover:button-gradient-hover hover:shadow-none"
            :title="$t('navbar.logout')"
          >
            <i class="fas fa-power-off"></i>
          </button>
        </div>
      </div>
    </div>
    <LanguageSwitcher v-model:open="langModalOpen" />
  </header>
</template>

<script>
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { LOCALES } from '@/i18n';

export default {
  name: 'Navbar',
  components: { LanguageSwitcher },
  data() {
    return {
      navigationOpen: false,
      stickyMenu: false,
      dropdown: false,
      supportUnreadCount: 0,
      langModalOpen: false,
      // Mobile scroll hide/show
      lastScrollY: 0,
      mobileNavHidden: false,
      isMobile: false,
      logoSrc: require('@/assets/isamurai_logo.png'),
    };
  },
  computed: {
    loggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    userInfo() {
      return this.$store.getters.userInfo;
    },
    userCredits() {
      return this.$store.getters.userCredits;
    },
    userCreditsMin() {
      return this.$store.getters.userCreditsMin;
    },
    planName() {
      return this.$store.getters.planName;
    },
    creditsMessage() {
      return this.$store.getters.creditsMessage;
    },
    userId() {
      return this.$store.getters.userId;
    },
    currentLangCode() {
      const code = (this.$i18n && this.$i18n.locale) || 'en';
      const map = { 'zh-Hans': 'ZH', 'en': 'EN', 'fr': 'FR', 'de': 'DE', 'es': 'ES', 'ja': 'JA' };
      return map[code] || code.toUpperCase();
    },
    locales() { return LOCALES; },
  },
  methods: {
    async logout() {
      this.$store.dispatch('logout').then(() => {
        this.$router.push('/login');
      });
    },
    loadUserCredits() {
      this.$store.dispatch('getUserCredits');
    },
    async fetchSupportUnread() {
      try {
        const token = this.$store.getters.accessToken;
        if (!token) return;
        const response = await fetch('/bo/support/api/unread-count/', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          this.supportUnreadCount = data.unread_count || 0;
        }
      } catch (e) {
        console.error('Failed to fetch support unread count', e);
      }
    },
    openLangModalFromMenu() {
      this.navigationOpen = false;
      this.langModalOpen = true;
    },
    // Mobile scroll behavior
    checkMobile() {
      this.isMobile = window.innerWidth < 1024;
      if (!this.isMobile) {
        this.mobileNavHidden = false;
      }
    },
    handleScroll() {
      this.stickyMenu = window.scrollY > 0;
      if (!this.isMobile) return;
      
      const currentScrollY = window.scrollY;
      if (Math.abs(currentScrollY - this.lastScrollY) < 10) return;
      
      if (currentScrollY > this.lastScrollY && currentScrollY > 60) {
        this.mobileNavHidden = true;
      } else if (currentScrollY < this.lastScrollY) {
        this.mobileNavHidden = false;
      }
      this.lastScrollY = currentScrollY;
    },
  },
  watch: {
    // Route change: close mobile menu + refresh support unread
    '$route'() {
      if (this.isMobile && this.navigationOpen) {
        this.navigationOpen = false;
      }
      if (this.loggedIn) {
        this.fetchSupportUnread();
      }
    },
    // Refresh when login state changes
    loggedIn(newVal) {
      if (newVal) {
        this.fetchSupportUnread();
        this._supportPoll = setInterval(() => this.fetchSupportUnread(), 30000);
      } else {
        if (this._supportPoll) clearInterval(this._supportPoll);
        this.supportUnreadCount = 0;
      }
    }
  },
  mounted() {
    this.$store.dispatch('loadSession');
    if (this.loggedIn) {
      this.loadUserCredits();
      this.fetchSupportUnread();
      this._supportPoll = setInterval(() => this.fetchSupportUnread(), 30000);
    }
    window.addEventListener('focus', this.fetchSupportUnread);
    window.addEventListener('focus', this.loadUserCredits);
    // Mobile scroll setup
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  },
  beforeUnmount() {
    if (this._supportPoll) clearInterval(this._supportPoll);
    window.removeEventListener('focus', this.fetchSupportUnread);
    window.removeEventListener('focus', this.loadUserCredits);
    window.removeEventListener('resize', this.checkMobile);
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>

<style scoped>
/* ================================================
   MOBILE ONLY STYLES (max-width: 1023px)
   ================================================ */
@media (max-width: 1023px) {
  /* Compact mobile navbar — override Tailwind py-7 (which puts the bar at
     ~96 px tall) with a much smaller padding so the navbar takes minimal
     vertical real estate on phones. */
  header.fixed {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }
  header.fixed .mobile-nav-buttons {
    gap: 8px !important;
  }
  header.fixed img[alt="Logo"] {
    width: 96px !important;
    height: auto !important;
  }
  /* Logo wrapper overrides inline min-width/margin so the right-side
     mobile-nav-buttons row (chip + lang + gallery + hamburger) has
     room on 320–360 px viewports. Without this, the hamburger gets
     pushed past the right edge and is only half-clickable. */
  .navbar-logo {
    min-width: 0 !important;
    margin-right: 12px !important;
    flex-shrink: 1 !important;
  }
  header.fixed .mobile-tutorial-btn {
    width: 28px !important; height: 28px !important;
    font-size: 13px !important;
  }
  /* Navbar hide/show on scroll */
  header.navbar-hidden {
    transform: translateY(-100%);
  }
  header {
    transition: transform 0.3s ease;
  }
  
  /* Mobile backdrop - dark overlay for click-to-close */
  .mobile-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99998;
    animation: fadeIn 0.3s ease;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  /* Mobile menu - ultra-compact side drawer (entire menu fits in one view) */
  .mobile-menu-panel {
    position: fixed !important;
    top: 0 !important;
    right: 0 !important;
    left: auto !important;
    bottom: 0 !important;
    width: 70% !important;
    max-width: 230px !important;
    height: 100vh !important;
    max-height: none !important;
    overflow-y: auto !important;
    background: linear-gradient(180deg, #1a1625 0%, #0f0d1a 100%) !important;
    border-left: 1px solid rgba(124, 58, 237, 0.2);
    z-index: 99999 !important;
    margin: 0 !important;
    padding: 36px 10px 10px 10px !important;
    border-radius: 0 !important;
    animation: slideInFromRight 0.3s ease forwards;
  }
  
  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  /* Mobile close button */
  .mobile-close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(124, 58, 237, 0.2);
    border: 1px solid rgba(124, 58, 237, 0.3);
    color: #a78bfa;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 100000;
  }
  
  .mobile-close-btn:hover {
    background: rgba(124, 58, 237, 0.4);
    color: white;
  }
  
  /* ================================================
     MOBILE MENU - HIDE REDUNDANT ITEMS
     ================================================ */
  
  /* Hide the standalone Tutorial icon (shown in dropdown instead) */
  .mobile-menu-panel .nav__menu:has(.tutorial-nav-icon) {
    display: none !important;
  }
  
  /* Hide the standalone Support icon (shown in dropdown instead) */
  .mobile-menu-panel .nav__menu:has(.support-nav-icon) {
    display: none !important;
  }
  
  /* Hide the standalone Credits display (shown in dropdown instead) */
  .mobile-menu-panel .nav__menu:has(.nav-gradient) {
    display: none !important;
  }
  
  /* Hide the Profile dropdown toggle arrow - we show content directly */
  .mobile-menu-panel .group > a svg {
    display: none !important;
  }
  
  /* Make Profile header look like a section title, not clickable */
  .mobile-menu-panel .group > a {
    pointer-events: none;
    opacity: 0.7;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 16px;
    padding-left: 0 !important;
    border-bottom: 1px solid rgba(124, 58, 237, 0.15);
    padding-bottom: 8px;
  }
  
  /* Mobile dropdown - flatten and show all items directly */
  .group .dropdown {
    display: flex !important;
    flex-direction: column;
    position: relative;
    background: transparent;
    box-shadow: none;
    padding: 0;
    margin-top: 8px;
    gap: 8px;
  }
  
  /* Profile dropdown items — tiny pill rows */
  .group .dropdown li a,
  .group .dropdown li span {
    display: flex !important;
    width: 100%;
    padding: 6px 8px !important;
    border-radius: 5px;
    background: rgba(124, 58, 237, 0.1);
    border: 1px solid rgba(124, 58, 237, 0.2);
    font-size: 0.68rem !important;
    line-height: 1.1 !important;
  }
  .group .dropdown { gap: 3px !important; margin-top: 3px !important; }

  /* Top-level menu rows — much tighter */
  .mobile-menu-panel ul,
  .mobile-menu-panel .nav__menu-list {
    gap: 0 !important;
  }
  .mobile-menu-panel .nav__menu {
    padding: 2px 0 !important;
    margin: 0 !important;
  }
  .mobile-menu-panel .nav__menu > a,
  .mobile-menu-panel .nav__menu > span {
    font-size: 0.72rem !important;
    padding: 6px 8px !important;
    line-height: 1.2 !important;
  }
  .mobile-menu-panel .nav__menu .fas {
    font-size: 0.7rem !important;
    width: 16px;
  }
  /* Profile section title */
  .mobile-menu-panel .group > a {
    font-size: 0.58rem !important;
    margin-top: 6px !important;
    padding-bottom: 3px !important;
  }
  /* Mobile close button — tiny */
  .mobile-close-btn {
    top: 6px !important;
    right: 6px !important;
    width: 24px !important;
    height: 24px !important;
    font-size: 0.8rem !important;
  }
  /* Drawer logo (if rendered inside the panel) */
  .mobile-menu-panel img[alt="Logo"] {
    max-width: 72px;
  }
  
  .group .dropdown li a:hover {
    background: rgba(124, 58, 237, 0.2);
  }
  
  /* Hide Plan text on mobile (shown in credits info) */
  .group .dropdown li:first-child {
    display: none !important;
  }
  
  /* Style the credits info nicely */
  .group .dropdown li:nth-child(2) span {
    background: linear-gradient(135deg, rgba(124, 58, 237, 0.2), rgba(168, 85, 247, 0.2));
    font-size: 0.9rem;
  }
  
  /* Mobile tutorial button (next to hamburger) */
  .mobile-tutorial-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(124, 58, 237, 0.2);
    border: 1px solid rgba(124, 58, 237, 0.3);
    color: #a78bfa;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .mobile-tutorial-btn:hover {
    background: rgba(124, 58, 237, 0.4);
    color: white;
  }

  /* Ultra-compact mobile credits chip — credits count only, with a
     single coin glyph. Sized to fit between Gallery and the
     hamburger without pushing the hamburger off-screen on a 320px
     viewport. */
  .mobile-credits-chip {
    display: inline-flex; align-items: center; gap: 3px;
    height: 26px;
    padding: 0 8px;
    border-radius: 999px;
    background: linear-gradient(135deg,
      rgba(124, 58, 237, 0.30),
      rgba(192, 38, 211, 0.30));
    border: 1px solid rgba(192, 38, 211, 0.45);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    text-decoration: none;
    flex: 0 0 auto;
    max-width: 80px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    box-shadow: 0 0 8px rgba(124, 58, 237, 0.22);
    transition: filter .15s, transform .1s;
  }
  .mobile-credits-chip:hover { filter: brightness(1.1); }
  .mobile-credits-chip:active { transform: scale(.97); }
  .mobile-credits-chip i {
    font-size: 10px;
    /* Pink → purple gradient on the coin glyph. FA icons are font
       glyphs, so paint via background-clip: text. */
    background: linear-gradient(135deg, #ec4899 0%, #c026d3 50%, #7c3aed 100%);
    -webkit-background-clip: text;
            background-clip: text;
    -webkit-text-fill-color: transparent;
            color: transparent;
    filter: drop-shadow(0 0 4px rgba(192, 38, 211, 0.5));
  }
  /* Allow the mobile-nav-buttons row to shrink chip first if space
     gets tight, so hamburger always stays in view. */
  .mobile-nav-buttons { gap: 6px !important; flex-wrap: nowrap; }

  /* Mobile language picker — same size as tutorial/gallery icons */
  .mobile-lang-btn {
    display: inline-flex; align-items: center; justify-content: center;
    gap: 3px;
    height: 26px; padding: 0 6px;
    border-radius: 999px;
    background: rgba(124, 58, 237, 0.20);
    border: 1px solid rgba(124, 58, 237, 0.35);
    color: #a78bfa;
    font-size: 10px; font-weight: 700;
    line-height: 1;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .mobile-lang-btn:hover { background: rgba(124, 58, 237, 0.4); color: #fff; }
  .mobile-lang-btn i { font-size: 10px; }
  .mobile-lang-btn .mobile-lang-code { font-size: 9px; letter-spacing: 0.4px; }
}
@media (min-width: 1024px) {
  .mobile-credits-chip { display: none !important; }
}

/* ================================================
   DESKTOP ONLY STYLES (min-width: 1024px)
   ================================================ */
@media (min-width: 1024px) {
  /* Hide mobile-only elements on desktop */
  .mobile-backdrop,
  .mobile-close-btn,
  .mobile-tutorial-btn {
    display: none !important;
  }
  
  /* Hide mobile buttons wrapper (hamburger + tutorial) on desktop */
  .mobile-nav-buttons {
    display: none !important;
  }
  
  /* Reset mobile menu panel styles on desktop */
  .mobile-menu-panel {
    position: relative !important;
    width: auto !important;
    max-width: none !important;
    height: auto !important;
    max-height: none !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    animation: none !important;
    transform: none !important;
  }
}
/* Dropdown Styles */
.dropdown {
  display: none;
  flex-direction: column;
}
.hidden {
  display: none;
}
.flex {
  display: flex;
}

.sml {
  font-size: 0.75rem !important;
  white-space: nowrap;
}

.nav__menu span {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav__menu .fas {
  font-size: 0.875rem;
}

.username-text {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.support-badge {
  background: #7e3af2;
  color: white;
  font-size: 0.65rem;
  padding: 0.125rem 0.4rem;
  border-radius: 10px;
  margin-left: 0.25rem;
  font-weight: 700;
}

.support-icon-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  transition: all 0.2s ease;
  text-decoration: none;
}

.support-icon-btn:hover {
  background: rgba(126, 58, 242, 0.6);
  color: #fff;
  transform: scale(1.05);
}

.support-icon-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ef4444;
  color: white;
  font-size: 0.6rem;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  border: 2px solid #111827;
}

.support-nav-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  transition: all 0.2s ease;
  text-decoration: none;
}

.support-nav-icon:hover {
  background: rgba(126, 58, 242, 0.5);
  color: #fff;
}

.support-nav-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  background: #ef4444;
  color: white;
  font-size: 0.55rem;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  border: 2px solid #111827;
}

.tutorial-nav-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(124, 58, 237, 0.2);
  border-radius: 50%;
  color: #a78bfa;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  text-decoration: none;
  border: 1px solid rgba(124, 58, 237, 0.3);
  cursor: pointer;
}

.tutorial-nav-icon:hover {
  background: rgba(124, 58, 237, 0.4);
  color: #fff;
  border-color: rgba(124, 58, 237, 0.6);
  transform: scale(1.05);
}

/* Desktop language picker pill */
.lang-nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(124, 58, 237, 0.2);
  border: 1px solid rgba(124, 58, 237, 0.3);
  color: #a78bfa;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.lang-nav-btn:hover {
  background: rgba(124, 58, 237, 0.4);
  color: #fff;
  border-color: rgba(124, 58, 237, 0.6);
  transform: scale(1.04);
}
.lang-nav-btn i { font-size: 0.85rem; }

/* Disabled tutorial icon (no tour available on this page) */
.tutorial-nav-icon.is-disabled,
.tutorial-nav-icon[disabled] {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: auto; /* keep tooltip available */
}
.tutorial-nav-icon.is-disabled:hover {
  background: rgba(124, 58, 237, 0.2);
  color: #a78bfa;
  border-color: rgba(124, 58, 237, 0.3);
  transform: none;
}

/* Collapse Gallery + Support icons into Profile dropdown on small desktop
   (<1280px). They remain in the top bar only when there is room. */
@media (min-width: 1024px) and (max-width: 1279px) {
  .collapsible-icon { display: none !important; }
}

/* Mobile: at very narrow viewports (<360px), hide the gallery icon — it is
   still reachable from the drawer + Profile dropdown. */
@media (max-width: 359px) {
  .mobile-nav-buttons .mobile-gallery-btn {
    display: none !important;
  }
}

/* Orange "Join for PromoCodes & Updates" CTA inside the Profile dropdown.
   Stands out from the purple-gradient menu items so users notice it. */
.promo-cta-li {
  margin-top: 0.25rem;
}
.promo-cta-btn {
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 1.125rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #ff8a00 0%, #ff5e00 100%);
  border: 1px solid rgba(255, 138, 0, 0.6);
  color: #fff !important;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(255, 94, 0, 0.35);
  transition: filter .15s ease, transform .1s ease, box-shadow .15s ease;
  white-space: nowrap;
}
.promo-cta-btn:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(255, 94, 0, 0.5);
  color: #fff !important;
}
.promo-cta-btn .fas { font-size: 0.875rem; }

/* Mobile drawer: the flattened-dropdown rule at the top of the file forces
   every `.group .dropdown li a` to a purple pill. Reassert orange here with
   higher specificity + match the smaller drawer typography. Tightened to
   match sibling pill density and stay inside the 230px drawer width even
   on the narrowest viewports. */
@media (max-width: 1023px) {
  .promo-cta-li { margin-top: 0.125rem !important; }
  .group .dropdown li a.promo-cta-btn {
    background: linear-gradient(135deg, #ff8a00 0%, #ff5e00 100%) !important;
    border: 1px solid rgba(255, 138, 0, 0.6) !important;
    color: #fff !important;
    font-size: 0.62rem !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    padding: 4px 8px !important;
    gap: 5px !important;
    box-shadow: 0 1px 4px rgba(255, 94, 0, 0.3) !important;
    line-height: 1.1 !important;
    white-space: nowrap;
    overflow: hidden;
    min-width: 0;
  }
  .group .dropdown li a.promo-cta-btn:hover {
    background: linear-gradient(135deg, #ff9a1f 0%, #ff6f1a 100%) !important;
  }
  .group .dropdown li a.promo-cta-btn .fas {
    font-size: 0.62rem !important;
    flex-shrink: 0;
  }
  .group .dropdown li a.promo-cta-btn span {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
}


/* account summary header in the profile dropdown */
.nav-acct { display: flex; align-items: center; gap: 10px; padding: 10px 14px 12px; margin-bottom: 6px;
  border-bottom: 1px solid rgba(124, 58, 237, 0.25); }
.nav-acct-avatar { width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0; display: grid; place-items: center;
  font-weight: 800; font-size: 15px; color: #fff; background: linear-gradient(135deg, #7c3aed, #ec4899); }
.nav-acct-info { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.nav-acct-name { color: #fff; font-size: 13.5px; line-height: 1.2; max-width: 160px; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; }
.nav-acct-meta { display: flex; align-items: center; gap: 8px; }
.nav-acct-plan { font-style: normal; font-size: 10.5px; font-weight: 700; letter-spacing: .04em; color: #fff;
  background: linear-gradient(90deg, #7c3aed, #a855f7); border-radius: 6px; padding: 2px 8px; }
.nav-acct-credits { font-style: normal; font-size: 11.5px; color: #e9d5ff; display: inline-flex; align-items: center; gap: 4px; }
</style>
